import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { Ticket } from '../ticket-boards/types'
import { objectify } from '@ezio/utils'
import { useEzioAPI } from '@ezio/services'

export const useTicketsStore = defineStore('tickets', () => {
    const { api } = useEzioAPI()

    const all = ref<{ [id: string]: Ticket }>({})

    const getByBoard = (boardId: string) => {
        return computed(() => {
            return Object.values(all.value).filter((t) => String(t.ticketBoardId) === String(boardId))
        })
    }

    const getByBoardGroupedByStatus = (boardId: string) => {
        return computed(() => {
            const tickets = Object.values(all.value).filter((t) => String(t.ticketBoardId) === String(boardId) && !t.archived)
            const grouped: { [statusId: string]: Ticket[] } = { unassigned: [] }

            for (const ticket of tickets) {
                const key = ticket.boardStatusId ? String(ticket.boardStatusId) : 'unassigned'
                if (!grouped[key]) {
                    grouped[key] = []
                }
                grouped[key].push(ticket)
            }

            // Sort tickets by position within each group, newest first when positions are equal
            for (const key in grouped) {
                grouped[key].sort((a, b) => {
                    const posA = a.position ?? 0
                    const posB = b.position ?? 0
                    if (posA !== posB) return posA - posB
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                })
            }

            return grouped
        })
    }

    const fetchMyTickets = async (userId: string) => {
        try {
            const response = await api.get('tickets', {
                assigned_to_user_id: userId,
                archived: 'all',
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
            })
            store(response.data)
            return response.data as Ticket[]
        } catch (error) {
            return []
        }
    }

    const fetchCreatedByMe = async (userId: string) => {
        try {
            const response = await api.get('tickets', {
                created_by_id: userId,
                archived: 'all',
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
            })
            store(response.data)
            return response.data as Ticket[]
        } catch (error) {
            return []
        }
    }

    const fetchByObject = async (objectType: string, objectId: string) => {
        try {
            const response = await api.get('tickets', {
                object_type: objectType,
                object_id: objectId,
                archived: 'all',
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
            })
            store(response.data)
            return response.data as Ticket[]
        } catch (error) {
            return []
        }
    }

    const fetchByBoard = async (boardId: string) => {
        try {
            const response = await api.get('tickets', {
                ticket_board_id: boardId,
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
            })
            store(response.data)
        } catch (error) {
            //
        }
    }

    const fetch = async (id: string) => {
        const response = await api.get('tickets/' + id, {
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
        })
        store([response.data])
        return response.data
    }

    const create = async (data: { title: string; description?: string; ticket_board_id: string; board_status_id?: string }) => {
        const response = await api.post('tickets', {
            ...data,
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
        })
        store([response.data])
        return response.data
    }

    const update = async (id: string, data: any) => {
        const response = await api.put('tickets/' + id, {
            ...data,
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
        })
        store([response.data])
        return response.data
    }

    const move = async (ticketId: string, boardStatusId: string | null) => {
        // Optimistic update
        const previous = all.value[ticketId]
        if (previous) {
            all.value[ticketId] = { ...previous, boardStatusId }
        }

        try {
            const response = await api.put('tickets/' + ticketId, {
                board_status_id: boardStatusId,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
            })
            store([response.data])
            return response.data
        } catch (error) {
            // Rollback
            if (previous) {
                all.value[ticketId] = previous
            }
            throw error
        }
    }

    const reorder = async (ticketId: string, boardStatusId: string | null, newIndex: number) => {
        const columnKey = boardStatusId ? String(boardStatusId) : 'unassigned'
        const ticket = all.value[ticketId]
        if (!ticket) return

        // Get tickets in the target column, sorted by position
        const columnTickets = Object.values(all.value)
            .filter((t) => {
                const key = t.boardStatusId ? String(t.boardStatusId) : 'unassigned'
                return key === columnKey && t.id !== ticketId
            })
            .sort((a, b) => (a.position || 0) - (b.position || 0))

        // Insert the moved ticket at newIndex
        const updatedTicket = { ...ticket, boardStatusId, position: newIndex }
        columnTickets.splice(newIndex, 0, updatedTicket)

        // Snapshot for rollback
        const previousStates: { [id: string]: Ticket } = {}
        const patchQueue: { id: string; details: any; position: number }[] = []

        // Reassign positions and collect changes
        for (let i = 0; i < columnTickets.length; i++) {
            const t = columnTickets[i]
            const oldPosition = t.position || 0
            if (oldPosition !== i || t.id === ticketId) {
                previousStates[t.id] = { ...all.value[t.id] }
                const newDetails = { ...(t.details || {}), position: i }
                // Optimistic update
                all.value[t.id] = { ...all.value[t.id], boardStatusId: t.id === ticketId ? boardStatusId : all.value[t.id].boardStatusId, position: i, details: newDetails }
                patchQueue.push({ id: t.id, details: newDetails, position: i })
            }
        }

        // Persist changes
        try {
            await Promise.all(
                patchQueue.map((item) =>
                    api.put('tickets/' + item.id, {
                        board_status_id: all.value[item.id].boardStatusId,
                        details: item.details,
                        include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
                    })
                )
            )
        } catch (error) {
            // Rollback
            for (const id in previousStates) {
                all.value[id] = previousStates[id]
            }
            throw error
        }
    }

    const addAttachment = async (ticketId: string, data: string) => {
        const response = await api.post('tickets/' + ticketId + '/attachments', {
            attachment: { data },
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
        })
        store([response.data])
    }

    const removeAttachment = async (ticketId: string, attachmentId: string) => {
        const response = await api.delete('tickets/' + ticketId + '/attachments', {
            attachment_id: attachmentId,
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments'
        })
        store([response.data])
    }

    const remove = async (id: string) => {
        await api.delete('tickets/' + id)
        const temp = { ...all.value }
        delete temp[id]
        all.value = temp
    }

    const store = (tickets: Ticket[]) => {
        const items = tickets.map((t) => Object.assign({}, all.value[t.id], t))
        all.value = Object.assign({}, all.value, objectify(items))
    }

    const reset = () => {
        all.value = {}
    }

    return {
        all,
        getByBoard,
        getByBoardGroupedByStatus,
        fetchMyTickets,
        fetchCreatedByMe,
        fetchByObject,
        fetchByBoard,
        fetch,
        create,
        update,
        move,
        reorder,
        addAttachment,
        removeAttachment,
        remove,
        reset,
        store
    }
})
